import React, { useEffect, useState, useContext } from 'react'
import ModalTest from '../../../BaseModal'
import { Button, ButtonContainer, CloseDiv, ContainerOfModal, Header, HeaderTitle, ViewContainer, ViewItem } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { X, Trash2 } from 'lucide-react'
import { Label } from '../../../styles/select-style'
import editPen from '../../../../assets/certificates/Tables/pencil.svg'
import { useTenant } from '../../../../contexts/TenantContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../ModalConfirmation'

interface Supplier {
  id: number
  companyName: string
}

interface Resource {
  id: number
  name: string
  code: string
  category: { id: number, name: string } | null
  type: { id: number, name: string } | null
  pricePerKg: number
  prices?: Array<{
    id: number
    price: string
    unitOfMeasurement: {
      id: number
      name: string
    }
  }>
  description: string
  suppliers: Supplier[]
  totalWeight?: number
  lengthInMm?: number
  unitQuantity?: number
  stockUnit?: string
  dimension1?: number | null
  dimension2?: number | null
  material?: string | null
  format?: string | null
  fabrication?: string | null
  ncm?: string | null
  shelf?: string | null
  stockLocation?: string | null
  stockStreet?: string | null
  colors?: Array<{ id: number; itemColorId: number; name: string; pantone?: { code: string; name?: string } | null }>
}

const ViewResourceModal = () => {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const navigate = useNavigate()
  const [resource, setResource] = useState<Resource | null>(null)
  const { setFeedback } = useContext(FeedbackContext)
  const { isFashionTenant } = useTenant()
  const itemName = isFashionTenant ? 'Aviamento' : 'Insumo'
  const resourceRedirectPath = isFashionTenant ? '/panel/stock/resource' : '/panel/resource'
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, resourceId: null as number | null, resourceName: '' })

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await ApiService.resources.findById(Number(id))
          const data = response.data
          setResource(data)
        } catch (error) {
          console.log(error)
        }
      }
    }
    fetchData()
  }, [id])

  const formatPrice = (price?: number | null) => {
    if (!price) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatPrices = (prices?: any[]) => {
    if (!prices || prices.length === 0) return 'N/A'

    return prices.map(price => {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(price.price))
      return `${formattedPrice}/${price.UnitOfMeasurement?.name || price.unitOfMeasurement?.name || 'N/A'}`
    }).join(', ')
  }

  const handleDeleteClick = () => {
    if (!id || !resource) return
    setDeleteModal({ isOpen: true, resourceId: parseInt(id), resourceName: resource.name })
  }

  const handleConfirmDelete = async () => {
    if (!deleteModal.resourceId) return
    try {
      await ApiService.resources.delete(deleteModal.resourceId)
      setFeedback({ 
        message: `${itemName} excluído com sucesso!`, 
        isOpen: true, 
        statusOk: true 
      })
  navigate(resourceRedirectPath)
    } catch (error: any) {
      let errorMessage = 'Erro ao excluir insumo'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      setFeedback({
        message: errorMessage,
        isOpen: true,
        statusOk: false
      })
    } finally {
      setDeleteModal({ isOpen: false, resourceId: null, resourceName: '' })
    }
  }

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, resourceId: null, resourceName: '' })
  }

  return (
    <>
      <ModalTest>
        <ContainerOfModal width='1100px'>
          <Header>
            <HeaderTitle>{itemName}: {resource?.name}</HeaderTitle>
            <CloseDiv onClick={() => navigate(resourceRedirectPath)}> <X size={20} /></CloseDiv>
          </Header>
          <ViewContainer style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <ViewItem>
              <span>Categoria</span>
              <Label>{resource?.category?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Tipo</span>
              <Label>{resource?.type?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Código</span>
              <Label>{resource?.code || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Preços</span>
              <Label>{formatPrices(resource?.prices)}</Label>
            </ViewItem>
            <ViewItem>
              <span>Fornecedores</span>
              <Label>{resource?.suppliers.map((supplier: any) => supplier.companyName).join(', ') || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Peso Total</span>
              <Label>{resource?.totalWeight != null ? resource.totalWeight : 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Comprimento (mm)</span>
              <Label>{resource?.lengthInMm != null ? resource.lengthInMm : 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Quantidade</span>
              <Label>{resource?.unitQuantity != null ? resource.unitQuantity : 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Unidade de Estoque</span>
              <Label>{resource?.stockUnit || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Dimensão 1</span>
              <Label>{resource?.dimension1 != null ? resource.dimension1 : 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Dimensão 2</span>
              <Label>{resource?.dimension2 != null ? resource.dimension2 : 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Material</span>
              <Label>{resource?.material || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Formato</span>
              <Label>{resource?.format || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Fabricação</span>
              <Label>{resource?.fabrication || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>NCM</span>
              <Label>{resource?.ncm || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Prateleira</span>
              <Label>{resource?.shelf || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Localização</span>
              <Label>{resource?.stockLocation || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Rua do Estoque</span>
              <Label>{resource?.stockStreet || 'N/A'}</Label>
            </ViewItem>
            {isFashionTenant && (
              <ViewItem>
                <span>Cores</span>
                <Label>
                  {resource?.colors && resource.colors.length > 0
                    ? resource.colors
                        .map(c => `${c.name}${c.pantone?.code ? ` (${c.pantone.code})` : ''}`)
                        .join(', ')
                    : 'N/A'}
                </Label>
              </ViewItem>
            )}
            <ViewItem>
              <span>Descrição</span>
              <Label>{resource?.description || 'N/A'}</Label>
            </ViewItem>
          </ViewContainer>
          <ButtonContainer>
            <Button
              onClick={handleDeleteClick}
              style={{
                backgroundColor: '#F87171',
                color: '#FFFFFF',
                border: '1px solid #F87171'
              }}
            >
              <Trash2 size={16} /> Excluir
            </Button>
            <Button
              onClick={() => navigate(`/panel/stock/resource/create?sourceId=${id}&variation=true`)}
              style={{ backgroundColor: '#8B5CF6', color: '#FFFFFF', border: '1px solid #8B5CF6' }}
            >
              Criar Variação
            </Button>
            <Button 
              onClick={() => navigate(`/panel/resource/edit?id=${id}`)}
              className='cancel'
            >
              <img src={editPen} alt='edit' /> Editar
            </Button>
          </ButtonContainer>
        </ContainerOfModal>
      </ModalTest>
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o {itemName.toLowerCase()}: &quot;{deleteModal.resourceName}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </>
  )
}

export default ViewResourceModal
